import React from "react";
import { Modal, Typography, Divider } from "antd";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import FormSelectInput from "components/FormItems/FormSelectInput";
import FormInput from "components/FormItems/FormInput";
import FormCheckBoxInput from "components/FormItems/FormCheckBoxInput";
import AppButton from "components/AppButton/AppButton";
import styles from "assets/css/modals.module.css";

// Validation schema
const validationSchema = Yup.object().shape({
  totalSms: Yup.number()
    .required("Total SMS is required")
    .min(0, "Total SMS must be non-negative"),
  totalMms: Yup.number()
    .required("Total MMS is required")
    .min(0, "Total MMS must be non-negative"),
  rateSms: Yup.number()
    .required("SMS rate is required")
    .min(0, "SMS rate must be non-negative"),
  rateMms: Yup.number()
    .required("MMS rate is required")
    .min(0, "MMS rate must be non-negative"),
  includeVirtualNumbers: Yup.boolean(),
  virtualNumberPackage: Yup.array().when("includeVirtualNumbers", {
    is: true,
    then: Yup.array().min(1, "At least one virtual number is required"),
    otherwise: Yup.array()
  })
});

const CreateInvoiceModal = ({ isModalVisible, handleModalClose, onSubmit, dedicatedNumbers = [], totalSms = 0, totalMms = 0, rateSms = 0, rateMms = 0 }) => {
  const initialValues = {
    totalSms: totalSms.toString(),
    totalMms: totalMms.toString(),
    rateSms: rateSms.toString(),
    rateMms: rateMms.toString(),
    includeVirtualNumbers: false,
    virtualNumberPackage: []
  };

  // Transform dedicated numbers data into dropdown options
  const virtualNumberPackageOptions = dedicatedNumbers.map(number => {
    const amount = number?.amount ? Number(number.amount) : 0;
    return {
      label: `${number.phone} - $${amount.toFixed(2)}`,
      value: number.phone,
      amount: amount
    };
  });

  // Get total amount for selected virtual numbers
  const getVirtualNumberAmount = selectedPhones => {
    if (!Array.isArray(selectedPhones)) return 0;

    return selectedPhones.reduce((total, phone) => {
      const selectedNumber = dedicatedNumbers.find(number => number.phone === phone);
      if (selectedNumber && selectedNumber.amount) {
        return total + Number(selectedNumber.amount);
      }
      return total;
    }, 0);
  };

  // Calculate SMS/MMS total
  const calculateSmsTotal = values => {
    const smsTotal = (parseFloat(values.totalSms) || 0) * (parseFloat(values.rateSms) || 0);
    const mmsTotal = (parseFloat(values.totalMms) || 0) * (parseFloat(values.rateMms) || 0);
    return smsTotal + mmsTotal;
  };

  // Calculate grand total
  const calculateGrandTotal = values => {
    const smsTotal = calculateSmsTotal(values);
    const virtualNumberAmount = getVirtualNumberAmount(values.virtualNumberPackage);
    return smsTotal + virtualNumberAmount;
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const virtualNumberAmount = getVirtualNumberAmount(values.virtualNumberPackage);
    const grandTotal = calculateGrandTotal(values);

    // Map selected virtual numbers to API format
    const numbers = values.virtualNumberPackage.map(phone => {
      const selectedNumber = dedicatedNumbers.find(number => number.phone === phone);
      return {
        number: phone,
        amount: selectedNumber?.amount ? selectedNumber.amount.toString() : "0"
      };
    });

    // Prepare data in API format
    const apiData = {
      numbers: numbers,
      totalSms: values.totalSms,
      totalMms: values.totalMms,
      perSmsRate: values.rateSms,
      perMmsRate: values.rateMms,
      dedicatedAmount: virtualNumberAmount.toString(),
      totalAmount: grandTotal.toString()
    };

    onSubmit(apiData);
    setSubmitting(false);
  };

  return (
    <Modal
      visible={isModalVisible}
      onCancel={handleModalClose}
      footer={null}
      maxWidth={800}
      centered
      title={
        <div className="text-center">
          <h4 className="pt-3 fw-bold">Create Invoice</h4>
        </div>
      }
      closeIcon={
        <div className={`btn ${styles.modalCloseBtn}`}>
          <i className="fas fa-times"></i>
        </div>
      }
      maskStyle={{ background: "linear-gradient(135deg, #00000050, #107c9850)" }}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
        {({ values, errors, touched, setFieldValue, handleChange, isSubmitting, handleSubmit: formikHandleSubmit }) => {
          return (
            <FormikForm onSubmit={formikHandleSubmit}>
              <div className="py-3">
                {/* Total SMS Input */}
                <div className="row">
                  <div className="col-4 mt-2">
                    <span className="fw-bold">Total SMS:</span>
                  </div>
                  <div className="col-8">
                    <FormInput
                      type="number"
                      placeholder="Enter total SMS"
                      value={values.totalSms}
                      onChange={e => setFieldValue("totalSms", e.target.value)}
                      size="default"
                      error={errors.totalSms}
                      touched={touched.totalSms}
                      name="totalSms"
                      inputProps={{ min: "0", step: "1" }}
                      required
                    />
                  </div>
                </div>

                {/* Total MMS Input */}
                <div className="row">
                  <div className="col-4 mt-2">
                    <span className="fw-bold">Total MMS:</span>
                  </div>
                  <div className="col-8">
                    <FormInput
                      type="number"
                      placeholder="Enter total MMS"
                      value={values.totalMms}
                      onChange={e => setFieldValue("totalMms", e.target.value)}
                      size="default"
                      error={errors.totalMms}
                      touched={touched.totalMms}
                      name="totalMms"
                      inputProps={{ min: "0", step: "1" }}
                      required
                    />
                  </div>
                </div>

                {/* Rate SMS Input */}
                <div className="row">
                  <div className="col-4 mt-2">
                    <span className="fw-bold">Rate SMS:</span>
                  </div>
                  <div className="col-8">
                    <FormInput
                      type="number"
                      placeholder="Enter SMS rate"
                      value={values.rateSms}
                      onChange={e => setFieldValue("rateSms", e.target.value)}
                      prefix="$"
                      size="default"
                      error={errors.rateSms}
                      touched={touched.rateSms}
                      name="rateSms"
                      inputProps={{ min: "0", step: "0.01" }}
                      required
                    />
                  </div>
                </div>

                {/* Rate MMS Input */}
                <div className="row">
                  <div className="col-4 mt-2">
                    <span className="fw-bold">Rate MMS:</span>
                  </div>
                  <div className="col-8">
                    <FormInput
                      type="number"
                      placeholder="Enter MMS rate"
                      value={values.rateMms}
                      onChange={e => setFieldValue("rateMms", e.target.value)}
                      prefix="$"
                      size="default"
                      error={errors.rateMms}
                      touched={touched.rateMms}
                      name="rateMms"
                      inputProps={{ min: "0", step: "0.01" }}
                      required
                    />
                  </div>
                </div>

                {/* SMS/MMS Total Display */}
                <div className="mb-4">
                  <div className="row">
                    <div className="col-4 mt-2">
                      <span className="fw-bold">Amount:</span>
                    </div>
                    <div className="col-8">
                      <div className="p-2 bg-light rounded">
                        <strong>${calculateSmsTotal(values).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Include Virtual Numbers Checkbox */}
                <div className="mb-3">
                  <FormCheckBoxInput
                    name="includeVirtualNumbers"
                    placeholder="Include Virtual Numbers"
                    values={values}
                    errors={errors.includeVirtualNumbers}
                    touched={touched.includeVirtualNumbers}
                    handleChange={e => {
                      handleChange(e);
                      if (!e.target.checked) {
                        setFieldValue("virtualNumberPackage", []);
                      }
                    }}
                    containerClassname="mb-0"
                  />
                </div>

                {/* Virtual Number Package Dropdown */}
                {values.includeVirtualNumbers && (
                  <div className="mb-4">
                    <div className="row">
                      <div className="col-4 mt-2">
                        <span className="fw-bold">Virtual Number:</span>
                      </div>
                      <div className="col-8">
                        <FormSelectInput
                          size="default"
                          mode="multiple"
                          placeholder="Select dedicated numbers"
                          value={values.virtualNumberPackage}
                          onChange={value => setFieldValue("virtualNumberPackage", value)}
                          error={errors.virtualNumberPackage}
                          touched={touched.virtualNumberPackage}
                          options={virtualNumberPackageOptions}
                          name="virtualNumberPackage"
                          containerProps={{ colon: false }}
                          required={values.includeVirtualNumbers}
                          allowClear={true}
                          showSearch={true}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Virtual Number Amount Display */}
                {values.includeVirtualNumbers && (
                  <div className="mb-4">
                    <div className="row">
                      <div className="col-4 mt-2">
                        <span className="fw-bold">Amount:</span>
                      </div>
                      <div className="col-8">
                        <div className="p-2 bg-light rounded">
                          <strong>${getVirtualNumberAmount(values.virtualNumberPackage).toFixed(2)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Divider />

                {/* Grand Total */}
                <div className="text-center mb-4">
                  <Typography.Title level={4} className="mb-0">
                    Grand Total (GST Excluded): ${calculateGrandTotal(values).toFixed(2)}
                  </Typography.Title>
                </div>

                {/* Action buttons */}
                <div className="d-flex gap-2 justify-content-center">
                  <AppButton withoutBg label="Cancel" onClick={handleModalClose} type="button" />
                  <AppButton label="Create Invoice" light type="submit" loading={isSubmitting} htmlType="submit" />
                </div>
              </div>
            </FormikForm>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateInvoiceModal;
